import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import MainLayout from '../layouts/MainLayout';
import { 
  Plus, 
  ArrowLeft, 
  Edit,
  Trash2,
  Upload,
  X,
  Package,
  FileText,
  Target,
  BookOpen,
  CheckCircle,
  Image as ImageIcon,
  Save
} from 'lucide-react';
import * as subProductService from '../services/subProductService';
import { createSlug } from '../services/categoryService';

interface SubProduct {
  _id?: string;
  id?: string;
  productId: string;
  slug?: { tr: string; en: string; es: string };
  title: { tr: string; en: string; es: string };
  summary: { tr: string; en: string; es: string };
  content?: {
    product_description?: { tr: string; en: string; es: string };
    technical_areas?: { tr: string; en: string; es: string };
    usage_areas?: { tr: string; en: string; es: string };
    usage_instructions?: { tr: string; en: string; es: string };
    advantages?: { tr?: string[]; en?: string[]; es?: string[] };
  };
  seo?: {
    metaTitle?: { tr: string; en: string; es: string };
    metaDescription?: { tr: string; en: string; es: string };
  };
  image?: string; // Geriye uyumluluk için
  images?: string[]; // Yeni API formatı (çoklu görsel)
  order?: number; // Sıralama değeri
  createdAt?: string;
  updatedAt?: string;
}

const SubProducts: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  
  const [parentProduct, setParentProduct] = useState<any>(null);
  const [subProducts, setSubProducts] = useState<SubProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSubProduct, setEditingSubProduct] = useState<SubProduct | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<'tr' | 'en' | 'es'>('tr');
  
  // Order editing state
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [tempOrderValues, setTempOrderValues] = useState<{ [key: string]: number }>({});
  
  // Form states
  const [formTitles, setFormTitles] = useState({ tr: '', en: '', es: '' });
  const [formSlugs, setFormSlugs] = useState({ tr: '', en: '', es: '' });
  const [formSummaries, setFormSummaries] = useState({ tr: '', en: '', es: '' });
  const [formDescriptions, setFormDescriptions] = useState({ tr: '', en: '', es: '' });
  const [formTechnicalAreas, setFormTechnicalAreas] = useState({ tr: '', en: '', es: '' });
  const [formUsageAreas, setFormUsageAreas] = useState({ tr: '', en: '', es: '' });
  const [formUsageInstructions, setFormUsageInstructions] = useState({ tr: '', en: '', es: '' });
  const [formAdvantages, setFormAdvantages] = useState({ tr: [''], en: [''], es: [''] });
  
  // SEO states
  const [formMetaTitles, setFormMetaTitles] = useState({ tr: '', en: '', es: '' });
  const [formMetaDescriptions, setFormMetaDescriptions] = useState({ tr: '', en: '', es: '' });
  
  // Order state
  const [formOrder, setFormOrder] = useState<number>(0);
  
  // Image states
  const [imageFiles, setImageFiles] = useState<(File | null)[]>([null]);
  const [imagePreviews, setImagePreviews] = useState<(string | null)[]>([null]);
  const [originalImages, setOriginalImages] = useState<string[]>([]);
  const [deletedImages, setDeletedImages] = useState<string[]>([]);

  // Load parent product and sub-products
  useEffect(() => {
    if (productId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      console.log('=== LOADING SUB-PRODUCTS ===');
      console.log('Product ID from URL:', productId);
      
      // Load all sub-products and filter by productId
      const allSubs = await subProductService.getSubProducts();
      console.log('Total sub-products received:', allSubs?.length || 0);
      
      // Filter sub-products for this product
      const currentProductId = productId;
      const filteredSubs = Array.isArray(allSubs) 
        ? allSubs.filter(sub => {
            const match = sub.productId === currentProductId;
            console.log(`Sub-product "${sub.title?.tr}" - productId: ${sub.productId} - Match: ${match}`);
            return match;
          })
        : [];
      
      console.log('=== FILTERED RESULT ===');
      console.log('Filtered count:', filteredSubs.length);
      
      // Sıralamayı order değerine göre yap (küçükten büyüğe)
      const sortedSubs = filteredSubs.sort((a, b) => {
        const orderA = a.order ?? 999999; // order yoksa en sona koy
        const orderB = b.order ?? 999999;
        return orderA - orderB;
      });
      
      setSubProducts(sortedSubs);
      
      // Set a simple parent product object for display
      // We don't need to fetch it from API since we only need the ID
      setParentProduct({
        _id: productId,
        title: { tr: 'Ürün', en: 'Product', es: 'Producto' }
      });
    } catch (error: any) {
      console.error('=== ERROR LOADING DATA ===');
      console.error('Error:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.message || 'Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleNewSubProduct = () => {
    setEditingSubProduct(null);
    setFormTitles({ tr: '', en: '', es: '' });
    setFormSlugs({ tr: '', en: '', es: '' });
    setFormSummaries({ tr: '', en: '', es: '' });
    setFormDescriptions({ tr: '', en: '', es: '' });
    setFormTechnicalAreas({ tr: '', en: '', es: '' });
    setFormUsageAreas({ tr: '', en: '', es: '' });
    setFormUsageInstructions({ tr: '', en: '', es: '' });
    setFormAdvantages({ tr: [''], en: [''], es: [''] });
    setFormMetaTitles({ tr: '', en: '', es: '' });
    setFormMetaDescriptions({ tr: '', en: '', es: '' });
    setFormOrder(0);
    setImageFiles([null]);
    setImagePreviews([null]);
    setOriginalImages([]);
    setDeletedImages([]);
    setSelectedLanguage('tr');
    setShowForm(true);
  };

  const handleEditSubProduct = (subProduct: SubProduct) => {
    console.log('=== EDITING SUB-PRODUCT ===');
    console.log('Sub-product data:', subProduct);
    console.log('SEO data:', subProduct.seo);
    
    setEditingSubProduct(subProduct);
    setFormTitles(subProduct.title || { tr: '', en: '', es: '' });
    setFormSlugs(subProduct.slug || { tr: '', en: '', es: '' });
    setFormSummaries(subProduct.summary || { tr: '', en: '', es: '' });
    setFormDescriptions(subProduct.content?.product_description || { tr: '', en: '', es: '' });
    setFormTechnicalAreas(subProduct.content?.technical_areas || { tr: '', en: '', es: '' });
    setFormUsageAreas(subProduct.content?.usage_areas || { tr: '', en: '', es: '' });
    setFormUsageInstructions(subProduct.content?.usage_instructions || { tr: '', en: '', es: '' });
    
    // Handle advantages with proper type checking
    const advantages = subProduct.content?.advantages;
    setFormAdvantages({
      tr: advantages?.tr && advantages.tr.length > 0 ? advantages.tr : [''],
      en: advantages?.en && advantages.en.length > 0 ? advantages.en : [''],
      es: advantages?.es && advantages.es.length > 0 ? advantages.es : ['']
    });
    
    // Handle SEO - Load from API
    setFormMetaTitles(subProduct.seo?.metaTitle || { tr: '', en: '', es: '' });
    setFormMetaDescriptions(subProduct.seo?.metaDescription || { tr: '', en: '', es: '' });
    
    // Handle Order
    setFormOrder(subProduct.order ?? 0);
    
    console.log('Loaded meta titles:', subProduct.seo?.metaTitle);
    console.log('Loaded meta descriptions:', subProduct.seo?.metaDescription);
    
    // Handle images - API'den images array veya image string gelebilir
    const existingImages = subProduct.images || (subProduct.image ? [subProduct.image] : []);
    setOriginalImages(existingImages);
    setImagePreviews(existingImages.length > 0 ? existingImages : [null]);
    setImageFiles(existingImages.map(() => null));
    setDeletedImages([]);
    
    setSelectedLanguage('tr');
    setShowForm(true);
  };

  const handleDeleteSubProduct = async (subProduct: SubProduct) => {
    if (!window.confirm('Bu alt ürünü silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const subProductId = subProduct._id || subProduct.id;
      if (!subProductId) {
        toast.error('Alt ürün ID bulunamadı');
        return;
      }

      await subProductService.deleteSubProduct(subProductId);
      toast.success('Alt ürün başarıyla silindi');
      loadData();
    } catch (error) {
      console.error('Error deleting sub-product:', error);
      toast.error('Alt ürün silinirken hata oluştu');
    }
  };

  // Alt ürün order güncelleme
  const handleUpdateSubProductOrder = async (subProductId: string, order: number) => {
    try {
      await subProductService.updateSubProductOrder(subProductId, order);
      toast.success('Alt ürün sıra numarası güncellendi');
      setEditingOrderId(null);
      // Temizle
      setTempOrderValues(prev => {
        const newValues = { ...prev };
        delete newValues[subProductId];
        return newValues;
      });
      await loadData(); // Listeyi yenile
    } catch (error) {
      console.error('Sub-product order update error:', error);
      toast.error('Alt ürün sıra numarası güncellenirken hata oluştu');
    }
  };

  const handleOrderChange = (subProductId: string, value: number) => {
    setTempOrderValues(prev => ({ ...prev, [subProductId]: value }));
  };

  const handleSaveOrder = (subProductId: string) => {
    if (tempOrderValues[subProductId] !== undefined) {
      handleUpdateSubProductOrder(subProductId, tempOrderValues[subProductId]);
    }
  };

  // Multi-image handling
  const handleImageSelect = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Görsel dosya boyutu 2MB\'dan küçük olmalıdır');
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast.error('Sadece görsel dosyaları yükleyebilirsiniz');
        return;
      }

      // Revoke old preview if exists
      if (imagePreviews[index] && imagePreviews[index]!.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviews[index]!);
      }

      const previewUrl = URL.createObjectURL(file);
      
      const newFiles = [...imageFiles];
      newFiles[index] = file;
      setImageFiles(newFiles);

      const newPreviews = [...imagePreviews];
      newPreviews[index] = previewUrl;
      setImagePreviews(newPreviews);
    }
  };

  const handleRemoveImage = (index: number) => {
    const imageUrl = imagePreviews[index];
    
    // If it's an original image, add to deleted list
    if (imageUrl && originalImages.includes(imageUrl)) {
      setDeletedImages(prev => [...prev, imageUrl]);
    }
    
    // Revoke blob URL if exists
    if (imageUrl && imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imageUrl);
    }

    // Remove from arrays
    const newFiles = imageFiles.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    
    // Ensure at least one slot
    if (newFiles.length === 0) {
      setImageFiles([null]);
      setImagePreviews([null]);
    } else {
      setImageFiles(newFiles);
      setImagePreviews(newPreviews);
    }
  };

  const addNewImageSlot = () => {
    setImageFiles(prev => [...prev, null]);
    setImagePreviews(prev => [...prev, null]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formTitles.tr.trim()) {
      toast.error('Lütfen Türkçe başlık girin');
      return;
    }

    if (!formSummaries.tr.trim()) {
      toast.error('Lütfen Türkçe özet girin');
      return;
    }

    try {
      setSubmitting(true);
      toast.loading('Kaydediliyor...', { id: 'saving' });

      const subProductData = {
        productId: productId!,
        slug: formSlugs, // Multi-language slug
        title: formTitles,
        summary: formSummaries,
        content: {
          product_description: formDescriptions,
          technical_areas: formTechnicalAreas,
          usage_areas: formUsageAreas,
          usage_instructions: formUsageInstructions,
          advantages: {
            tr: formAdvantages.tr.filter(adv => adv.trim() !== ''),
            en: formAdvantages.en.filter(adv => adv.trim() !== ''),
            es: formAdvantages.es.filter(adv => adv.trim() !== '')
          }
        },
        seo: {
          metaTitle: formMetaTitles,
          metaDescription: formMetaDescriptions
        },
        order: formOrder
      };

      let savedSubProduct;
      let subProductId;
      
      if (editingSubProduct) {
        subProductId = editingSubProduct._id || editingSubProduct.id;
        if (!subProductId) {
          toast.error('Alt ürün ID bulunamadı', { id: 'saving' });
          return;
        }
        
        // Delete removed images first using bulk delete API
        if (deletedImages.length > 0) {
          toast.loading(`Silinen görseller temizleniyor... (${deletedImages.length})`, { id: 'saving' });
          try {
            console.log('Deleting images:', deletedImages);
            await subProductService.bulkDeleteImages(deletedImages);
            console.log('Successfully deleted images from S3');
          } catch (error) {
            console.error('Error deleting images:', error);
            toast.error('Bazı görseller silinemedi', { id: 'saving' });
            // Continue with the update even if delete fails
          }
        }
        
        savedSubProduct = await subProductService.updateSubProduct(subProductId, subProductData);
        toast.success('Alt ürün başarıyla güncellendi', { id: 'saving' });
      } else {
        savedSubProduct = await subProductService.createSubProduct(productId!, subProductData);
        
        // API response'dan ID'yi al
        subProductId = savedSubProduct._id || savedSubProduct.id;
        
        console.log('=== SUB-PRODUCT CREATED ===');
        console.log('Response:', savedSubProduct);
        console.log('Extracted ID:', subProductId);
        
        // Eğer response'da ID yoksa, yeni oluşturulan sub-product'ı fetch et
        if (!subProductId) {
          console.log('No ID in response, fetching all sub-products to find the new one...');
          toast.loading('Alt ürün bilgileri alınıyor...', { id: 'saving' });
          
          // Tüm sub-product'ları fetch et ve en son oluşturulanı bul
          const allSubProducts = await subProductService.getSubProducts();
          const currentProductSubs = allSubProducts.filter((sub: any) => sub.productId === productId);
          
          // En son oluşturulan sub-product'ı bul (createdAt'e göre sırala)
          const sortedSubs = currentProductSubs.sort((a: any, b: any) => {
            const dateA = new Date(a.createdAt || 0).getTime();
            const dateB = new Date(b.createdAt || 0).getTime();
            return dateB - dateA; // En yeni önce
          });
          
          if (sortedSubs.length > 0) {
            const newestSub = sortedSubs[0];
            subProductId = newestSub._id || newestSub.id;
            console.log('Found newest sub-product:', newestSub);
            console.log('Using ID:', subProductId);
          }
        }
        
        toast.success('Alt ürün başarıyla oluşturuldu', { id: 'saving' });
      }

      // Upload new images (çoklu görsel - tek istekte)
      console.log('=== IMAGE UPLOAD SECTION ===');
      console.log('Sub-product ID:', subProductId);
      console.log('Image files state:', imageFiles);
      
      if (subProductId) {
        const filesToUpload = imageFiles.filter(file => file !== null) as File[];
        console.log('Files to upload:', filesToUpload.length);
        console.log('Files:', filesToUpload);
        
        if (filesToUpload.length > 0) {
          try {
            toast.loading(`${filesToUpload.length} görsel yükleniyor...`, { id: 'saving' });
            
            // Tüm görselleri tek istekte yükle
            const uploadResult = await subProductService.uploadSubProductImages(subProductId, filesToUpload);
            console.log('Images upload result:', uploadResult);
            
            toast.success(`${filesToUpload.length} görsel başarıyla yüklendi`, { id: 'saving' });
          } catch (error) {
            console.error('Error uploading images:', error);
            toast.error('Görseller yüklenirken hata oluştu', { id: 'saving' });
          }
        } else {
          console.log('No images to upload');
        }
      } else {
        console.error('No sub-product ID found for image upload!');
        toast.error('Alt ürün ID bulunamadı, görseller yüklenemedi', { id: 'saving' });
      }

      setShowForm(false);
      loadData();
    } catch (error: any) {
      console.error('Error saving sub-product:', error);
      toast.error(error.message || 'Alt ürün kaydedilirken hata oluştu', { id: 'saving' });
    } finally {
      setSubmitting(false);
    }
  };

  // Advantage management
  const addAdvantage = (lang: 'tr' | 'en' | 'es') => {
    setFormAdvantages(prev => ({
      ...prev,
      [lang]: [...prev[lang], '']
    }));
  };

  const removeAdvantage = (lang: 'tr' | 'en' | 'es', index: number) => {
    setFormAdvantages(prev => ({
      ...prev,
      [lang]: prev[lang].filter((_, i) => i !== index)
    }));
  };

  const updateAdvantage = (lang: 'tr' | 'en' | 'es', index: number, value: string) => {
    setFormAdvantages(prev => ({
      ...prev,
      [lang]: prev[lang].map((item, i) => i === index ? value : item)
    }));
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-gray-600">Yükleniyor...</span>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/products')}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Ürünlere Dön
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Alt Ürünler
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {parentProduct?.title?.tr || 'Ürün'} - {subProducts.length} alt ürün
                </p>
              </div>
            </div>
            
            <button
              onClick={handleNewSubProduct}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Yeni Alt Ürün
            </button>
          </div>
        </div>

        {/* Sub-Products List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Alt Ürün Listesi</h2>
          </div>

          {subProducts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Henüz alt ürün bulunmuyor.
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {subProducts.map((subProduct) => (
                <div key={subProduct._id || subProduct.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-4">
                    {/* Image */}
                    <div className="h-20 w-20 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                      {(subProduct.images && subProduct.images.length > 0) || subProduct.image ? (
                        <img
                          src={(subProduct.images && subProduct.images[0]) || subProduct.image || ''}
                          alt="Sub-product"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Package className="h-8 w-8 text-gray-400" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {subProduct.title?.tr || 'İsimsiz Alt Ürün'}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {subProduct.summary?.tr || ''}
                      </p>
                      <div className="flex items-center gap-4 mt-2">
                        {subProduct.createdAt && (
                          <p className="text-xs text-gray-500">
                            Oluşturulma: {new Date(subProduct.createdAt).toLocaleDateString('tr-TR')}
                          </p>
                        )}
                        <div className="flex items-center gap-2">
                          <label className="text-xs font-medium text-gray-700">Sıra:</label>
                          <input
                            type="number"
                            min="0"
                            value={
                              editingOrderId === (subProduct._id || subProduct.id)
                                ? tempOrderValues[subProduct._id || subProduct.id!] ?? subProduct.order ?? 0
                                : subProduct.order ?? 0
                            }
                            onChange={(e) => {
                              const subProductId = subProduct._id || subProduct.id;
                              if (subProductId) {
                                setEditingOrderId(subProductId);
                                handleOrderChange(subProductId, parseInt(e.target.value) || 0);
                              }
                            }}
                            className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                            onClick={(e) => e.stopPropagation()}
                          />
                          {editingOrderId === (subProduct._id || subProduct.id) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const subProductId = subProduct._id || subProduct.id;
                                if (subProductId) {
                                  handleSaveOrder(subProductId);
                                }
                              }}
                              className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                              title="Kaydet"
                            >
                              <Save className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditSubProduct(subProduct)}
                        className="p-2 text-primary-600 hover:bg-primary-100 rounded-md transition-colors"
                        title="Düzenle"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteSubProduct(subProduct)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                        title="Sil"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sub-Product Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[95vh] flex flex-col relative">
            {/* Loading Overlay */}
            {submitting && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">İşlem yapılıyor, lütfen bekleyin...</p>
                </div>
              </div>
            )}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <Package className="h-6 w-6 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingSubProduct ? 'Alt Ürün Düzenle' : 'Yeni Alt Ürün Ekle'}
                </h2>
              </div>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Language Selector */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Düzenleme Dili
                  </label>
                  <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg max-w-md">
                    {[
                      { code: 'tr', name: 'Türkçe' },
                      { code: 'en', name: 'English' },
                      { code: 'es', name: 'Español' }
                    ].map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setSelectedLanguage(lang.code as 'tr' | 'en' | 'es')}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex-1 ${
                          selectedLanguage === lang.code
                            ? 'bg-white text-primary-700 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Sol Kolon - Temel Bilgiler */}
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <FileText className="h-4 w-4 mr-2 text-primary-600" />
                        Temel Bilgiler
                      </h3>

                      {/* Title */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Alt Ürün Adı ({selectedLanguage.toUpperCase()})
                          {selectedLanguage === 'tr' && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                          type="text"
                          value={formTitles[selectedLanguage]}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            setFormTitles(prev => ({ ...prev, [selectedLanguage]: newValue }));
                            // Slug'ı otomatik oluştur
                            setFormSlugs(prev => ({ ...prev, [selectedLanguage]: createSlug(newValue) }));
                          }}
                          className={`w-full px-2 py-1.5 text-sm border rounded-md focus:ring-1 focus:border-transparent ${
                            selectedLanguage === 'tr' && (!formTitles.tr || formTitles.tr.trim() === '')
                              ? 'border-red-300 focus:ring-red-500'
                              : 'border-gray-300 focus:ring-primary-500'
                          }`}
                          placeholder={selectedLanguage === 'tr' ? "Alt ürün adını girin (zorunlu)" : "Alt ürün adını girin"}
                          required={selectedLanguage === 'tr'}
                        />
                        {selectedLanguage === 'tr' && (!formTitles.tr || formTitles.tr.trim() === '') && (
                          <p className="text-red-500 text-xs mt-1">
                            Türkçe alt ürün adı zorunludur
                          </p>
                        )}
                      </div>

                      {/* Slug */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          URL Slug ({selectedLanguage.toUpperCase()})
                          <span className="text-gray-500 text-xs ml-1">(Otomatik oluşturulur, düzenlenebilir)</span>
                        </label>
                        <input
                          type="text"
                          value={formSlugs[selectedLanguage] || ''}
                          onChange={(e) => setFormSlugs(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent bg-gray-50"
                          placeholder={`${selectedLanguage}-slug-ornek`}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Alt ürün adından otomatik oluşturulur. Türkçe karakterler İngilizce'ye çevrilir.
                        </p>
                      </div>

                      {/* Summary */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Alt Ürün Özeti ({selectedLanguage.toUpperCase()})
                          {selectedLanguage === 'tr' && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <textarea
                          rows={2}
                          value={formSummaries[selectedLanguage]}
                          onChange={(e) => setFormSummaries(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Kısa alt ürün özetini girin"
                          required={selectedLanguage === 'tr'}
                        />
                      </div>
                    </div>

                    {/* Detaylı İçerik Alanları */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <BookOpen className="h-4 w-4 mr-2 text-primary-600" />
                        Detaylı İçerik
                      </h3>

                      {/* Product Description */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Ürün Açıklaması ({selectedLanguage.toUpperCase()})
                        </label>
                        <textarea
                          rows={3}
                          value={formDescriptions[selectedLanguage]}
                          onChange={(e) => setFormDescriptions(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Ürünün detaylı açıklamasını girin"
                        />
                      </div>

                      {/* Technical Areas */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Teknik Özellikler ({selectedLanguage.toUpperCase()})
                        </label>
                        <textarea
                          rows={2}
                          value={formTechnicalAreas[selectedLanguage]}
                          onChange={(e) => setFormTechnicalAreas(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Teknik özellikleri girin (virgülle ayırın)"
                        />
                      </div>

                      {/* Usage Areas */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Kullanım Alanları ({selectedLanguage.toUpperCase()})
                        </label>
                        <textarea
                          rows={2}
                          value={formUsageAreas[selectedLanguage]}
                          onChange={(e) => setFormUsageAreas(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Kullanım alanlarını girin (virgülle ayırın)"
                        />
                      </div>

                      {/* Usage Instructions */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Kullanım Talimatları ({selectedLanguage.toUpperCase()})
                        </label>
                        <textarea
                          rows={2}
                          value={formUsageInstructions[selectedLanguage]}
                          onChange={(e) => setFormUsageInstructions(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Kullanım talimatlarını girin"
                        />
                      </div>

                      {/* Advantages */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Avantajlar ({selectedLanguage.toUpperCase()})
                        </label>
                        <div className="space-y-1.5">
                          {formAdvantages[selectedLanguage].map((advantage, index) => (
                            <div key={index} className="flex gap-1.5">
                              <input
                                type="text"
                                value={advantage}
                                onChange={(e) => updateAdvantage(selectedLanguage, index, e.target.value)}
                                className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                                placeholder={`Avantaj ${index + 1}`}
                              />
                              {formAdvantages[selectedLanguage].length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeAdvantage(selectedLanguage, index)}
                                  className="px-2 py-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addAdvantage(selectedLanguage)}
                            className="w-full px-2 py-1.5 text-xs text-primary-600 border border-primary-300 rounded-md hover:bg-primary-50 flex items-center justify-center"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Avantaj Ekle
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sağ Kolon - SEO ve Görseller */}
                  <div className="space-y-4">
                    {/* Sıralama */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <Package className="h-4 w-4 mr-2 text-primary-600" />
                        Sıralama
                      </h3>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Sıra Numarası
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formOrder}
                          onChange={(e) => setFormOrder(parseInt(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                          placeholder="0"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Küçük numara önce gösterilir (örn: 1, 2, 3...)
                        </p>
                      </div>
                    </div>

                    {/* SEO */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <Target className="h-4 w-4 mr-2 text-primary-600" />
                        SEO Ayarları
                      </h3>

                      {/* SEO Title */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          SEO Başlığı ({selectedLanguage.toUpperCase()})
                        </label>
                        <input
                          type="text"
                          value={formMetaTitles[selectedLanguage]}
                          onChange={(e) => setFormMetaTitles(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                          placeholder="SEO için optimize edilmiş başlık"
                        />
                      </div>

                      {/* SEO Description */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          SEO Açıklaması ({selectedLanguage.toUpperCase()})
                        </label>
                        <textarea
                          rows={2}
                          value={formMetaDescriptions[selectedLanguage]}
                          onChange={(e) => setFormMetaDescriptions(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Arama motorları için açıklama (150-160 karakter)"
                        />
                      </div>
                    </div>

                    {/* Multiple Image Upload */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                          <ImageIcon className="h-4 w-4 mr-2 text-primary-600" />
                          Alt Ürün Görselleri
                        </h3>
                        <button
                          type="button"
                          onClick={addNewImageSlot}
                          className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-md hover:bg-primary-200 flex items-center"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Görsel Ekle
                        </button>
                      </div>
                      
                      <div className="space-y-2">
                        {imagePreviews.map((preview, index) => (
                          <div key={index} className="border border-gray-200 rounded-md p-2">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-gray-700">
                                {index + 1}. Görsel
                              </span>
                              {imagePreviews.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveImage(index)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                            
                            {preview ? (
                              <div className="relative">
                                <img
                                  src={preview}
                                  alt={`Sub-product preview ${index + 1}`}
                                  className="w-full h-24 object-cover rounded border border-gray-300"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemoveImage(index)}
                                  className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 shadow-lg"
                                >
                                  <X className="h-2 w-2" />
                                </button>
                              </div>
                            ) : (
                              <div
                                onClick={() => document.getElementById(`sub-product-image-input-${index}`)?.click()}
                                className="w-full h-24 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors"
                              >
                                <Upload className="h-4 w-4 text-gray-400 mb-1" />
                                <p className="text-xs text-gray-600">Görsel seçin</p>
                              </div>
                            )}
                            
                            <input
                              id={`sub-product-image-input-${index}`}
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageSelect(index, e)}
                              className="hidden"
                            />
                          </div>
                        ))}
                        
                        {imagePreviews.length === 0 && (
                          <div
                            onClick={addNewImageSlot}
                            className="w-full h-32 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors"
                          >
                            <Upload className="h-6 w-6 text-gray-400 mb-1" />
                            <p className="text-xs text-gray-600 font-medium">İlk görseli eklemek için tıklayın</p>
                            <p className="text-xs text-gray-500">PNG, JPG, GIF (Max 5MB)</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fixed Form Actions */}
              <div className="flex justify-between items-center border-t border-gray-200 bg-gray-50 px-4 py-3 rounded-b-lg flex-shrink-0">
                <div className="text-xs text-gray-600">
                  <span className="text-red-500">*</span> Türkçe alanlar zorunludur
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1.5"></div>
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-3 w-3 mr-1.5" />
                        {editingSubProduct ? 'Güncelle' : 'Oluştur'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default SubProducts;

